import React, { CSSProperties } from 'react';
import { Stack, SxProps, Theme, Typography } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { organizationAtom } from 'services/organization/atom';
import { useFetchCustomerContractDashboardUrl } from 'modules/analytics/api/useFetchCustomerContractDashboardUrl';
import { isSafariBrowser } from 'core/utils/isSafariBrowser';
import { AlertCard } from 'common/ui';
import { Loading } from 'components/Loading';
import { AdministratorLayout } from './components';

const isSafari = isSafariBrowser();

const safariWarningContainer: SxProps<Theme> = {
  p: 4,
  width: '60%'
};

const iframeStyle: CSSProperties = {
  border: 'none',
  height: '80vh',
  width: '100%'
};

type DashboardProps = {
  isLoading: boolean;
  url: string | undefined;
};

const Dashboard: React.FC<DashboardProps> = ({ isLoading, url }) => {
  if (isSafari) {
    return (
      <Stack sx={safariWarningContainer}>
        <AlertCard type="warning" title="Safari browser detected">
          <Typography variant="default">
            The reporting dashboard is currently not supported in Safari. For
            the best experience, please switch to a different browser.
          </Typography>
        </AlertCard>
      </Stack>
    );
  }

  if (isLoading || !url) {
    return <Loading />;
  }

  return (
    <iframe src={url} style={iframeStyle} title="Customer contract dashboard" />
  );
};

const noAccountContainer: SxProps<Theme> = {
  p: 4,
  width: '60%'
};

export const AdministratorContractDashboardTab: React.FC = () => {
  const organization = useRecoilValue(organizationAtom);
  const { data, isLoading } = useFetchCustomerContractDashboardUrl();

  if (!organization) {
    return (
      <AdministratorLayout>
        <Stack sx={noAccountContainer}>
          <AlertCard type="info" title="No accounts available">
            <Typography variant="default">
              All accounts are currently hidden. Please unhide at least one
              account to view the contract dashboard.
            </Typography>
          </AlertCard>
        </Stack>
      </AdministratorLayout>
    );
  }

  const loading = isLoading || !data?.url;

  return (
    <AdministratorLayout>
      <Dashboard isLoading={loading} url={data?.url} />
    </AdministratorLayout>
  );
};
